import type { NodeDefinition } from '../../types';
import { t, r } from '../../rubyParts';
import {
  DEFAULT_WALKING_MOTION_ID, DEFAULT_WALKING_STEPS, getWalkingLabel, normalizeWalkingData,
} from '../../config/walkingMotions';
import { WalkingNodeView } from '../views/WalkingNode';
import { WalkingSettings } from '../settings/WalkingSettings';
import { runMotionCard } from './motionCardRun';
import { nextFlowRequestId } from '../../atoms/flowRequestId';
import {
  createWalkingTimeline, getWalkingDirection, getWalkingDirectionLabel, getWalkingStepsLabel, getWalkingStepUnit,
} from '../../animation/walkingMotion';
import { getTimelineDurationMs, STANDBY_TRANSITION_DURATION_MS } from '../../animation/animationUtils';

const VIRTUAL_MOTION_TIMEOUT_MS = 30_000;
const BLOCKED_NOTICE_DURATION_MS = 2_000;

export const walkingDefinition: NodeDefinition<'walking'> = {
  label: 'Walking',
  showInPalette: true,
  paletteColor: 'bg-teal-100 border-teal-500 text-teal-700',
  miniMapColor: '#0d9488',
  defaultData: { label: 'Walking', motionId: DEFAULT_WALKING_MOTION_ID, stepCount: DEFAULT_WALKING_STEPS },
  view: WalkingNodeView,
  settings: WalkingSettings,
  run: async (node, ctx, log) => {
    const data = normalizeWalkingData(node.data);
    const motionId = data.motionId ?? DEFAULT_WALKING_MOTION_ID;
    const motionLabel = getWalkingLabel(motionId) ?? '（未設定）';
    const motionNo = Number(motionId);
    const steps = data.stepCount;
    const direction = getWalkingDirection(motionNo);
    if (steps !== undefined && !direction) {
      throw new Error('歩数指定は前進・後進・左右移動・カーブ歩行でのみ使用できます。');
    }
    const robotMethod = ({
      forward: 'walkForwardSteps',
      backward: 'walkBackwardSteps',
      left: 'walkLeftSteps',
      right: 'walkRightSteps',
      curveRight: 'walkCurveRightSteps',
      curveLeft: 'walkCurveLeftSteps',
    } as const)[direction ?? 'forward'];
    if (steps !== undefined && ctx.robotService && !ctx.robotService[robotMethod]) {
      throw new Error(`この接続では${getWalkingStepUnit(direction!)}数指定の${getWalkingDirectionLabel(direction!)}を実機へ送信できません。`);
    }
    const timeline = steps === undefined || !direction ? undefined : createWalkingTimeline(direction, steps);
    const forwardKeys = timeline?.movement.find(track => track.id === 'z')!.keyframes;
    const lateralKeys = timeline?.movement.find(track => track.id === 'x')!.keyframes;
    const yawKeys = timeline?.movement.find(track => track.id === 'yaw')!.keyframes;
    // Maze travel is measured along the motion's own direction, which is reversed for backward walking.
    const sign = direction === 'backward' ? -1 : 1;
    const displacement = timeline ? {
      forward: -sign * forwardKeys!.at(-1)!.value,
      lateral: sign * lateralKeys!.at(-1)!.value,
      path: forwardKeys!.map((key, index) => ({ forward: -sign * key.value, lateral: sign * lateralKeys![index].value })),
      ...(yawKeys!.at(-1)!.value ? { yaw: yawKeys!.at(-1)!.value } : {}),
    } : undefined;
    ctx.setNodeRuntimeState?.(node.id, null);
    if (ctx.mazeControl && !(displacement
      ? ctx.mazeControl.canApplyWalkingMotion(motionNo, displacement)
      : ctx.mazeControl.canApplyWalkingMotion(motionNo))) {
      ctx.setNodeRuntimeState?.(node.id, 'blocked');
      if (ctx.setSayText) {
        await new Promise<void>((resolve) => {
          ctx.setSayText!({
            text: '壁があるため通れません',
            duration: BLOCKED_NOTICE_DURATION_MS,
            requestId: nextFlowRequestId(),
            onComplete: resolve,
          });
        });
      }
      return;
    }
    await runMotionCard({
      logTag: 'Walking',
      nodeKind: 'walking',
      nodeId: node.id,
      motionId,
      motionLabel: steps === undefined || !direction ? motionLabel : getWalkingStepsLabel(direction, steps),
      virtualTimeoutMs: timeline
        ? getTimelineDurationMs(timeline) + STANDBY_TRANSITION_DURATION_MS + VIRTUAL_MOTION_TIMEOUT_MS
        : VIRTUAL_MOTION_TIMEOUT_MS,
      timeline,
      robotCommand: steps === undefined ? undefined
        : (robotService, options) => robotService[robotMethod]!(steps, options),
      requireVirtualCompletion: steps !== undefined && !ctx.robotService,
    }, ctx, log);

    if (!ctx.mazeControl || ctx.signal?.aborted) return;
    const result = await (displacement
      ? ctx.mazeControl.applyWalkingMotion(motionNo, displacement)
      : ctx.mazeControl.applyWalkingMotion(motionNo));
    if (result.blocked) {
      log('[Walking] かべにぶつかり、そこで止まりました');
      ctx.onExecutionEvent?.({ type: 'collision', nodeId: node.id });
    }
    if (result.goalReached) {
      log('[Walking] ゴールに到着しました！');
      ctx.onExecutionEvent?.({ type: 'goal-reached', nodeId: node.id });
      if (!ctx.mazeControl.continueFlowAfterGoal) {
        return { nextHandle: null };
      }
    }
  },
  help: {
    title: [t('Walking（'), r('歩', 'ある'), t('く）')],
    description: [
      r('選', 'えら'),
      t('んだ'),
      r('動', 'うご'),
      t('きで、ロボットが'),
      r('歩', 'ある'),
      t('いたり'),
      r('向', 'む'),
      t('きを'),
      r('変', 'か'),
      t('えたりします。'),
      r('動', 'うご'),
      t('きが'),
      r('終', 'お'),
      t('わってから、つぎのカードへ'),
      r('進', 'すす'),
      t('みます。'),
      r('迷路', 'めいろ'),
      t('では、ロボットの'),
      r('位置', 'いち'),
      t('と'),
      r('向', 'む'),
      t('きも'),
      r('変', 'か'),
      t('わります。'),
    ],
  },
};